import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { fetchCurrentTournament, fetchTournamentGames, fetchLeagueHistory, finalizeTournament, fetchActiveLeagues, fetchLeagueTournament, changeTournamentTeam } from '../../api/tournament';
import { startGame, startGameWithSubs } from '../../api/games';
import { fetchUserTeams } from '../../api/userTeams';
import { fetchPlayoffStatus } from '../../api/playoff';
import PlayoffPromoCard from '../../components/PlayoffPromoCard';
import { fetchTeamByID } from '../../api/userTeams';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import FatiguedSubstitutionModal from '../../components/FatiguedSubstitutionModal';
import { useAtom } from 'jotai';
import { currentUserAtom, authLoadedAtom } from '../../auth/AuthAtoms';
import { sortTeamLineup } from '../../helpers/teamHelpers';
import { getCardRarityClass } from '../../utils/cardUtils';
import CardPreviewModal from '../../components/CardPreviewModal';
import { getVariationAbbrev } from '../../helpers/cardHelpers';
import { handleActiveGameExists } from '../../helpers/startGameConflicts';

const RANK_CLASSES = ['rank-1', 'rank-2', 'rank-3'];

const LeaderboardRow = ({ entry, index, isYou, interactive, onViewLineup, onChallenge, canChallenge, isRevenge }) => {
    const isCpu = !entry.User;
    return (
        <div
            key={entry.tournamentEntryID}
            className={`tournament-row${index % 2 === 1 ? ' tournament-row-stripe' : ''}${isYou ? ' tournament-row-you' : ''}`}
        >
            <span className={`t-rank ${RANK_CLASSES[index] || ''}`}>{entry.rank}</span>
            <span
                className={`t-team${interactive ? ' t-team-clickable' : ''}`}
                onClick={interactive ? () => onViewLineup(entry) : undefined}
            >
                {entry.UserTeam?.name || '—'}
            </span>
            <span className="t-player">
                {isCpu ? <FontAwesomeIcon icon={faRobot} title="CPU" /> : <a href={`/u/${entry.User.username}`} target="_blank" rel="noopener noreferrer">{entry.User.username}</a>}
            </span>
            <span className="t-elo">{entry.currentElo}</span>
            <span className="t-record">{entry.wins}-{entry.losses}</span>
            {interactive && (
                <span className="t-challenge">
                    {!isYou && canChallenge?.(entry) && (
                        <button
                            className={isRevenge?.(entry) ? 'danger-action' : 'less-action'}
                            onClick={() => onChallenge(entry)}
                        >
                            {isRevenge?.(entry) ? 'Revenge' : 'Challenge'}
                        </button>
                    )}
                </span>
            )}
        </div>
    );
};

const TournamentCountdown = ({ date }) => {
    const [timeLeft, setTimeLeft] = useState('');
    const [urgent, setUrgent] = useState(false);

    useEffect(() => {
        const computeTimeLeft = () => {
            // Tournament date is YYYY-MM-DD — ends at 5:00 AM UTC of the NEXT day (9 PM PST)
            const endTime = new Date(date + 'T05:00:00Z');
            endTime.setUTCDate(endTime.getUTCDate() + 1);
            const now = new Date();
            const diff = endTime.getTime() - now.getTime();
            if (diff <= 0) return { text: 'Finalizing...', urgent: true };
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            return {
                text: `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`,
                urgent: hours < 1,
            };
        };

        const update = () => {
            const result = computeTimeLeft();
            setTimeLeft(result.text);
            setUrgent(result.urgent);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [date]);

    return (
        <span className={`tournament-countdown${urgent ? ' countdown-urgent' : ''}`}>
            <span className="countdown-label">~</span>{timeLeft}
            <span className="countdown-tooltip">Approximate — actual end time may vary</span>
        </span>
    );
};

const TournamentPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const [authLoaded] = useAtom(authLoadedAtom);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [tournament, setTournament] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [userEntry, setUserEntry] = useState(null);
    const [tab, setTab] = useState('leaderboard');
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const historyLoadedRef = useRef(false);
    const [games, setGames] = useState(null);
    const [gamesLoading, setGamesLoading] = useState(false);
    const gamesLoadedRef = useRef(false);

    // League tabs
    const [leagues, setLeagues] = useState([]);
    const [userLeague, setUserLeague] = useState(null);
    const [activeLeague, setActiveLeague] = useState(null);
    const leagueReqIdRef = useRef(0);

    // Active (unfinished) tournament game — blocks new challenges
    const [activeGameID, setActiveGameID] = useState(null);
    const [defeatedByEntryIDs, setDefeatedByEntryIDs] = useState(new Set());
    const [playoffRun, setPlayoffRun] = useState(null);

    // Challenge flow
    const [challengeTarget, setChallengeTarget] = useState(null);
    const [challenging, setChallenging] = useState(false);

    // Fatigue substitution state (409 modal flow)
    const [showSubModal, setShowSubModal] = useState(false);
    const [fatiguedData, setFatiguedData] = useState(null);
    const [pendingPayload, setPendingPayload] = useState(null);

    // Change team modal
    const [showChangeTeam, setShowChangeTeam] = useState(false);
    const [userTeams, setUserTeams] = useState([]);
    const [changingTeam, setChangingTeam] = useState(false);

    // Lineup preview modal
    const [lineupPreview, setLineupPreview] = useState(null);
    const [lineupLoading, setLineupLoading] = useState(false);
    const [previewCard, setPreviewCard] = useState(null);

    const loadTournament = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchCurrentTournament();
            setTournament(result.tournament);
            setLeaderboard(result.leaderboard ?? []);
            setUserEntry(result.userEntry);
            setActiveGameID(result.activeGameID ?? null);
            setDefeatedByEntryIDs(new Set(result.defeatedByEntryIDs ?? []));
            if (result.userLeague != null) {
                setUserLeague(result.userLeague);
                setActiveLeague(result.userLeague);
            }
            setLoadError(false);
        } catch (error) {
            if (error.response?.status === 404) {
                setTournament(null);
            } else {
                setLoadError(true);
                toast.error('Failed to load tournament');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!authLoaded) return;
        loadTournament();
        fetchPlayoffStatus().then(data => setPlayoffRun(data?.run ?? null)).catch(() => {});
        fetchActiveLeagues()
            .then(data => setLeagues(data.leagues ?? []))
            .catch(err => console.error('Failed to load leagues:', err));
    }, [loadTournament, authLoaded, currentUser?.userID]);

    useEffect(() => {
        if (activeLeague != null) return;
        const fallback = userLeague ?? leagues[0]?.league;
        if (fallback != null) setActiveLeague(fallback);
    }, [activeLeague, userLeague, leagues]);

    const handleLeagueChange = useCallback(async (league) => {
        const reqId = ++leagueReqIdRef.current;
        setActiveLeague(league);
        setLoadError(false);

        // Reset tab-specific caches so switching leagues re-fetches
        historyLoadedRef.current = false;
        gamesLoadedRef.current = false;
        setHistory(null);
        setGames(null);

        if (league === userLeague) {
            // Reload the user's own tournament data (includes userEntry, etc.)
            loadTournament();
            return;
        }

        // Viewing another league — load that league's tournament + leaderboard
        setLoading(true);
        try {
            const data = await fetchLeagueTournament(league);
            if (leagueReqIdRef.current === reqId) {
                setTournament(data?.tournament ?? null);
                setLeaderboard(data?.leaderboard ?? []);
                setUserEntry(null);
                setActiveGameID(null);
                setDefeatedByEntryIDs(new Set());
            }
        } catch {
            if (leagueReqIdRef.current === reqId) {
                toast.error('Failed to load league');
                setTournament(null);
                setLeaderboard([]);
            }
        } finally {
            if (leagueReqIdRef.current === reqId) setLoading(false);
        }
    }, [userLeague, loadTournament]);

    const handleTabChange = useCallback((newTab) => {
        setTab(newTab);
        if (newTab === 'history' && !historyLoadedRef.current) {
            historyLoadedRef.current = true;
            setHistoryLoading(true);
            fetchLeagueHistory(activeLeague)
                .then(data => setHistory(Array.isArray(data) ? data : []))
                .catch(() => {
                    historyLoadedRef.current = false;
                    toast.error('Failed to load history');
                })
                .finally(() => setHistoryLoading(false));
        }
        if (newTab === 'games' && !gamesLoadedRef.current) {
            gamesLoadedRef.current = true;
            setGamesLoading(true);
            fetchTournamentGames(activeLeague)
                .then(data => setGames(Array.isArray(data) ? data : []))
                .catch(() => {
                    gamesLoadedRef.current = false;
                    toast.error('Failed to load games');
                })
                .finally(() => setGamesLoading(false));
        }
    }, [activeLeague]);

    const handleChallenge = useCallback((entry) => {
        setChallengeTarget(entry);
    }, []);

    const handleConfirmChallenge = useCallback(async () => {
        if (!challengeTarget) return;
        setChallenging(true);
        try {
            const result = await startGame({ opponentEntryID: challengeTarget.tournamentEntryID });
            setChallengeTarget(null);
            navigate(`/game/${result.gameID}/play`);
        } catch (error) {
            if (handleActiveGameExists(error, navigate)) {
                setChallengeTarget(null);
                return;
            }
            if (error.response?.status === 409 && error.response?.data?.code === 'PLAYERS_FATIGUED') {
                setFatiguedData({
                    unavailableCards: error.response.data.unavailableCards,
                    availableSubstitutes: error.response.data.availableSubstitutes,
                });
                setPendingPayload(error.response.data.pendingPayload);
                setChallengeTarget(null);
                setShowSubModal(true);
            } else if (error.response?.status === 409 && error.response?.data?.gameID) {
                // Unfinished game — redirect to it
                setChallengeTarget(null);
                navigate(`/game/${error.response.data.gameID}/play`);
            } else if (error.response?.status === 409) {
                toast.error(error.response.data.reason || 'Invalid challenge');
                setChallengeTarget(null);
            } else {
                toast.error('Failed to start game');
                setChallengeTarget(null);
            }
        } finally {
            setChallenging(false);
        }
    }, [challengeTarget, navigate]);

    const handleSubConfirm = useCallback(async (subs) => {
        setShowSubModal(false);
        setChallenging(true);
        try {
            const result = await startGameWithSubs({ ...pendingPayload, substitutions: subs });
            navigate(`/game/${result.gameID}/play`);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to start game after substitutions';
            toast.error(msg);
        } finally {
            setChallenging(false);
            setFatiguedData(null);
            setPendingPayload(null);
        }
    }, [pendingPayload, navigate]);

    const handleSubCancel = useCallback(() => {
        setShowSubModal(false);
        setFatiguedData(null);
        setPendingPayload(null);
    }, []);

    const handleAllHealed = useCallback(async () => {
        setShowSubModal(false);
        setFatiguedData(null);
        setChallenging(true);
        try {
            const result = await startGame(pendingPayload);
            navigate(`/game/${result.gameID}/play`);
        } catch (err) {
            if (handleActiveGameExists(err, navigate)) return;
            toast.error(err.response?.data?.error || 'Failed to start game');
        } finally {
            setChallenging(false);
            setPendingPayload(null);
        }
    }, [pendingPayload, navigate]);

    const handleViewLineup = useCallback(async (entry) => {
        if (!entry.UserTeam?.userTeamID) return;
        setLineupLoading(true);
        setLineupPreview({ name: entry.UserTeam.name, lineups: null });
        try {
            const team = await fetchTeamByID(entry.UserTeam.userTeamID);
            setLineupPreview({ name: team.name, lineups: team.UserTeamLineups });
        } catch {
            toast.error('Failed to load team lineup');
            setLineupPreview(null);
        } finally {
            setLineupLoading(false);
        }
    }, []);

    const handleOpenChangeTeam = useCallback(async () => {
        setShowChangeTeam(true);
        try {
            const teams = await fetchUserTeams();
            setUserTeams(teams);
        } catch {
            toast.error('Failed to load teams');
            setShowChangeTeam(false);
        }
    }, []);

    const handleChangeTeam = useCallback(async (userTeamID) => {
        setChangingTeam(true);
        try {
            await changeTournamentTeam(userTeamID);
            toast.success('Team changed');
            setShowChangeTeam(false);
            loadTournament();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to change team');
        } finally {
            setChangingTeam(false);
        }
    }, [loadTournament]);

    const userRank = userEntry
        ? leaderboard.findIndex(e => String(e.tournamentEntryID) === String(userEntry.tournamentEntryID)) + 1
        : 0;

    const isRevenge = useCallback((entry) => {
        return defeatedByEntryIDs.has(String(entry.tournamentEntryID));
    }, [defeatedByEntryIDs]);

    const canChallenge = useCallback((entry) => {
        if (!userEntry) return false;
        if (activeGameID) return false;
        if (String(entry.tournamentEntryID) === String(userEntry.tournamentEntryID)) return false;
        // Revenge bypasses rank distance
        if (isRevenge(entry)) return true;
        const opponentRank = entry.rank;
        if (!userRank || !opponentRank) return false;
        return Math.abs(userRank - opponentRank) <= 10;
    }, [userEntry, userRank, activeGameID, isRevenge]);

    const activeLeagueInfo = leagues.find(l => l.league === activeLeague);
    const leagueDisplayName = activeLeagueInfo?.leagueName ?? tournament?.name ?? 'Daily Championship';

    return (
        <>
        {leagues.length > 0 && (
            <div className="league-tabs">
                {leagues.map(l => (
                    <button
                        key={l.league}
                        className={`league-tab${activeLeague === l.league ? ' active' : ''}${userLeague === l.league ? ' user-league' : ''}`}
                        onClick={() => handleLeagueChange(l.league)}
                    >
                        {l.leagueName}
                        {userLeague === l.league && <span className="league-you-badge">You</span>}
                    </button>
                ))}
            </div>
        )}
        <div className="tournament-container">
            <div className="banner-header">
                <h1>{leagueDisplayName} — Daily Championship</h1>
                {tournament?.date && <TournamentCountdown date={tournament.date} />}
                {currentUser?.isAdmin && (
                    <button
                        className="boring_button"
                        onClick={async () => {
                            if (!window.confirm('Are you sure you want to force-finalize the tournament? This will promote/relegate players and create new tournaments.')) return;
                            try {
                                const result = await finalizeTournament({ force: true });
                                toast.success(result.message || 'Tournament finalized');
                            } catch (e) {
                                toast.error(e.message || 'Finalize failed');
                            }
                        }}
                    >
                        Finalize
                    </button>
                )}
            </div>

            {loading ? (
                <LoadingSpinner />
            ) : loadError ? (
                <div className="tournament-empty">
                    Something went wrong loading the tournament. Please try refreshing.
                </div>
            ) : !tournament ? (
                <div className="tournament-empty">No active tournament for this league.</div>
            ) : (
            <>
            <div className="playoff-promo-wrapper">
                <PlayoffPromoCard run={playoffRun} />
            </div>

            {userEntry ? (
                <div className="tournament-entry-strip">
                    <div className="entry-strip-info">
                        <span className="entry-team-name">
                            {userEntry.UserTeam?.name || 'Your Team'}
                        </span>
                        {userRank > 0 && (
                            <div className="entry-stat entry-rank">
                                <span className="entry-stat-value">#{userRank}</span>
                                <span className="entry-stat-label">Rank</span>
                            </div>
                        )}
                        <div className="entry-stat">
                            <span className="entry-stat-value">{userEntry.currentElo}</span>
                            <span className="entry-stat-label">ELO</span>
                        </div>
                        <div className="entry-stat">
                            <span className="entry-stat-value">{userEntry.wins}-{userEntry.losses}</span>
                            <span className="entry-stat-label">W-L</span>
                        </div>
                        <div className="entry-stat">
                            <span className="entry-stat-value">{userEntry.gamesPlayed}</span>
                            <span className="entry-stat-label">Games</span>
                        </div>
                    </div>
                    <button
                        className="boring_button"
                        onClick={handleOpenChangeTeam}
                        disabled={!!activeGameID}
                    >
                        Change Team
                    </button>
                    {activeGameID && (
                        <div className="entry-strip-active-game">
                            <span>You have a game in progress</span>
                            <button
                                className="less-action"
                                onClick={() => navigate(`/game/${activeGameID}/play`)}
                            >
                                Resume Game
                            </button>
                        </div>
                    )}
                </div>
            ) : activeLeague === userLeague ? (
                <div className="tournament-empty">
                    You&apos;re not entered in today&apos;s tournament. Entries are automatic — check back tomorrow!
                </div>
            ) : null}

            {/* Tabs */}
            <div className="tournament-tabs">
                <button
                    className={`tournament-tab ${tab === 'leaderboard' ? 'active' : ''}`}
                    onClick={() => handleTabChange('leaderboard')}
                >
                    Leaderboard
                </button>
                <button
                    className={`tournament-tab ${tab === 'games' ? 'active' : ''}`}
                    onClick={() => handleTabChange('games')}
                >
                    Games
                </button>
                <button
                    className={`tournament-tab ${tab === 'history' ? 'active' : ''}`}
                    onClick={() => handleTabChange('history')}
                >
                    History
                </button>
            </div>

            {/* Leaderboard tab */}
            {tab === 'leaderboard' && (
                <div className="tournament-leaderboard">
                    {leaderboard.length === 0 ? (
                        <div className="tournament-empty">No entries yet.</div>
                    ) : (
                        <div className="tournament-list">
                            {leaderboard.map((entry, i) => (
                                <LeaderboardRow
                                    key={entry.tournamentEntryID}
                                    entry={entry}
                                    index={i}
                                    isYou={!!(userEntry && String(entry.tournamentEntryID) === String(userEntry.tournamentEntryID))}
                                    interactive={true}
                                    onViewLineup={handleViewLineup}
                                    onChallenge={handleChallenge}
                                    canChallenge={canChallenge}
                                    isRevenge={isRevenge}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Games tab */}
            {tab === 'games' && (
                <div className="tournament-games">
                    {gamesLoading ? (
                        <LoadingSpinner />
                    ) : !games || games.length === 0 ? (
                        <div className="tournament-empty">No games played yet.</div>
                    ) : (
                        <div className="tournament-games-list">
                            {games.map(game => {
                                const participants = game.GameParticipants || [];
                                const home = participants.find(p => p.role === 'home');
                                const away = participants.find(p => p.role === 'away');
                                const scoreMatch = game.resultDescription?.match(/H:\s*(\d+)\s*-\s*V:\s*(\d+)/);
                                const homeScore = scoreMatch ? scoreMatch[1] : '?';
                                const awayScore = scoreMatch ? scoreMatch[2] : '?';

                                // User team on left, CPU opponent on right
                                const userTeam = participants.find(p => p.userID) || away;
                                const cpuTeam = participants.find(p => !p.userID) || home;
                                const userScore = userTeam === home ? homeScore : awayScore;
                                const cpuScore = cpuTeam === home ? homeScore : awayScore;

                                const renderElo = (participant) => {
                                    if (participant?.tournamentEloBefore == null || participant?.tournamentEloAfter == null) return null;
                                    const delta = participant.tournamentEloAfter - participant.tournamentEloBefore;
                                    return (
                                        <span className={`elo-delta ${delta >= 0 ? 'elo-up' : 'elo-down'}`}>
                                            {delta >= 0 ? '+' : ''}{delta}
                                        </span>
                                    );
                                };

                                const gameDate = new Date(game.created);
                                const timeStr = gameDate.toLocaleDateString([], { month: 'short', day: 'numeric' })
                                    + ', ' + gameDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

                                return (
                                    <div key={game.gameID} className="games-row" onClick={() => navigate(`/game/${game.gameID}/summary`)}>
                                        <div className="games-matchup">
                                            <div className="games-team">
                                                <span className="games-team-name">{userTeam?.UserTeam?.name || 'You'}</span>
                                                {renderElo(userTeam)}
                                            </div>
                                            <div className="games-score">
                                                <span>{userScore}</span>
                                                <span className="games-score-sep">-</span>
                                                <span>{cpuScore}</span>
                                            </div>
                                            <div className="games-team games-team-right">
                                                <span className="games-team-name">{cpuTeam?.UserTeam?.name || 'Opponent'}</span>
                                                {renderElo(cpuTeam)}
                                            </div>
                                        </div>
                                        <span className="games-time">{timeStr}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* History tab */}
            {tab === 'history' && (
                <div className="tournament-history">
                    {historyLoading ? (
                        <LoadingSpinner />
                    ) : !history || history.length === 0 ? (
                        <div className="tournament-empty">No past tournaments for this league yet.</div>
                    ) : (
                        history.map(tournament => (
                            <div
                                key={tournament.tournamentID}
                                className="history-entry history-entry-clickable"
                                onClick={() => navigate(`/tournament/${tournament.tournamentID}/results`)}
                            >
                                <span className="history-name">
                                    {tournament.name || 'Tournament'}
                                </span>
                                {tournament.date ? (
                                    <span className="history-stats">
                                        <span>{new Date(tournament.date).toLocaleDateString()}</span>
                                    </span>
                                ) : null}
                            </div>
                        ))
                    )}
                </div>
            )}

            </>
            )}

            {/* Challenge confirmation modal */}
            {challengeTarget && (
                <Modal onClose={() => setChallengeTarget(null)} title="Challenge">
                    <div className="challenge-confirm">
                        <h3>Challenge Opponent</h3>
                        <div className="challenge-matchup">
                            <div className="challenge-team">
                                <div className="team-name">{userEntry?.UserTeam?.name || 'You'}</div>
                                <div className="team-elo">{userEntry?.currentElo} ELO</div>
                            </div>
                            <span className="challenge-vs">VS</span>
                            <div className="challenge-team">
                                <div className="team-name">
                                    {challengeTarget.UserTeam?.name || 'Opponent'}
                                    {!challengeTarget.User && (
                                        <> <FontAwesomeIcon icon={faRobot} title="CPU" /></>
                                    )}
                                </div>
                                <div className="team-elo">{challengeTarget.currentElo} ELO</div>
                            </div>
                        </div>
                        <div className="challenge-actions">
                            <button
                                onClick={handleConfirmChallenge}
                                disabled={challenging}
                            >
                                {challenging ? 'Starting...' : 'Play Ball'}
                            </button>
                            <button
                                className="boring_button"
                                onClick={() => setChallengeTarget(null)}
                                disabled={challenging}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Lineup preview modal */}
            {lineupPreview && (
                <Modal onClose={() => setLineupPreview(null)} title="Team Lineup">
                    <div className="lineup-preview-modal">
                        <h3>{lineupPreview.name}</h3>
                        {lineupLoading || !lineupPreview.lineups ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="lineup-preview-list">
                                {sortTeamLineup(lineupPreview.lineups).map(lineup => (
                                    <div key={lineup.lineupID} className="lineup-row">
                                        {lineup.lineupOrder && (
                                            <span className="batting-order bold">{lineup.lineupOrder}</span>
                                        )}
                                        <span className="position">{lineup.position}</span>
                                        <span className="lineup-card-link" onClick={() => setPreviewCard(lineup.Card)}>
                                            <span className={`rarity-dot ${getCardRarityClass(lineup.Card.rating)}`}></span>
                                            ({lineup.Card.rating}) {lineup.Card.year} {lineup.Card.name}
                                            {lineup.Card.customImage && (
                                                <span className="variation-label">ART</span>
                                            )}
                                            {getVariationAbbrev(lineup.Card) && (
                                                <span className="variation-label">{getVariationAbbrev(lineup.Card)}</span>
                                            )}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Card preview modal */}
            {previewCard && (
                <CardPreviewModal card={previewCard} onClose={() => setPreviewCard(null)} />
            )}

            {/* Fatigue substitution modal */}
            {showSubModal && fatiguedData && (
                <FatiguedSubstitutionModal
                    unavailableCards={fatiguedData.unavailableCards}
                    availableSubstitutes={fatiguedData.availableSubstitutes}
                    onConfirm={handleSubConfirm}
                    onCancel={handleSubCancel}
                    onAllHealed={handleAllHealed}
                    title="Some Players Need Rest"
                />
            )}

            {/* Change team modal */}
            {showChangeTeam && (
                <Modal onClose={() => setShowChangeTeam(false)} title="Change Team">
                    <div className="change-team-modal">
                        {userTeams.length === 0 ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="change-team-list">
                                {userTeams.map(team => (
                                    <button
                                        key={team.userTeamID}
                                        className={`change-team-option${String(team.userTeamID) === String(userEntry?.userTeamID) ? ' current' : ''}`}
                                        onClick={() => handleChangeTeam(team.userTeamID)}
                                        disabled={changingTeam || String(team.userTeamID) === String(userEntry?.userTeamID)}
                                    >
                                        {team.name}
                                        {String(team.userTeamID) === String(userEntry?.userTeamID) && <span className="current-badge">Current</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
        </>
    );
};

export default TournamentPage;
